/**
 * Validation schema + resolver for the Create Chat form.
 *
 * Ported from the legacy create-chat form
 * (`choco-admin-chats/src/components/NewChatForm/CreateChat/validation.ts`).
 * The legacy resolver was commented out during a merge, which let incomplete or
 * malformed data reach `chatCreate`. This restores a typed React Hook Form
 * resolver enforcing the active legacy rules:
 *
 * - buyer, distributor and at least one buyer user are required
 * - additional emails must be valid comma-separated addresses
 * - a customer number is required when the linked distributor mandates one
 *   (carried through the distributor option's `metaData.customerNumberMandatory`)
 * - MOV amount and currency are validated as a pair (either both set or neither)
 */

import { CreateChatType } from '@choco-admin/types';
import type {
  BuyerSelectOption,
  BuyerUserSelectOption,
  ChatTypeOption,
  CreateChatFormData,
  CreatedContextOption,
  CutoffTime,
  CutoffType,
  Day,
  SubCatalogOption,
  SupplierUserSelectOption,
  UserSelectOption,
} from '@choco-admin/types';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Resolver } from 'react-hook-form';
import * as yup from 'yup';

import { validateEmails } from '../utils/emailValidation';

export interface CreateChatValidationOptions {
  /** Whether the selected distributor requires a customer number. */
  customerNumberMandatory: boolean;
}

/** True for a non-empty string (mirrors the legacy `!isNil(val) && val !== ''`). */
const isPresent = (value: string | null | undefined): boolean =>
  value != null && value !== '';

const isNonEmptyArray = <TItem>(value: TItem[] | null | undefined): boolean =>
  Array.isArray(value) && value.length > 0;

/**
 * Builds the Create Chat validation schema. `customerNumberMandatory` is
 * resolved per-submission from the selected distributor, so the schema is
 * parameterised rather than static (legacy parity).
 */
export const getCreateChatValidationSchema = ({
  customerNumberMandatory,
}: CreateChatValidationOptions) => {
  const customerNumber = customerNumberMandatory
    ? yup
        .string()
        .defined()
        .required('Customer number is required for this chat')
    : yup.string().defined();

  return yup.object().shape(
    {
      // Participants
      buyer: yup
        .mixed<BuyerSelectOption>()
        .required('You must select a buyer entity'),
      supplier: yup
        .mixed<UserSelectOption>()
        .required('You must select a distributor entity'),
      buyerUsers: yup
        .array(yup.mixed<BuyerUserSelectOption>().defined())
        .defined()
        .test(
          'buyerUsersMin',
          'You must select at least one buyer user',
          (value) => isNonEmptyArray(value)
        ),
      supplierUsers: yup
        .array(yup.mixed<SupplierUserSelectOption>().defined())
        .defined(),
      deselectedOrderPreferences: yup.array(yup.string().defined()).defined(),

      // Settings
      chatName: yup.string().defined(),
      restaurantName: yup.string().defined(),
      deliveryAddress: yup.string().defined(),
      customerNumber,
      additionalEmails: yup
        .string()
        .defined()
        .test('additionalEmails', 'Enter valid email addresses', (value) =>
          validateEmails(value ?? '')
        ),
      // Only ordering chats are created here; support chats are set up
      // elsewhere. The create form locks the type to ordering, so this is a
      // defensive guard against a support type reaching `chatCreate`.
      chatType: yup
        .mixed<ChatTypeOption>()
        .nullable()
        .defined()
        .test(
          'orderingOnly',
          'Support chats cannot be created here',
          (value) => value?.value !== CreateChatType.SUPPORT
        ),
      subCatalog: yup.array(yup.mixed<SubCatalogOption>().defined()).defined(),
      createdContext: yup.mixed<CreatedContextOption>().nullable().defined(),
      // MOV amount/currency form a pair: setting one requires the other.
      customerLevelMovAmount: yup
        .string()
        .nullable()
        .when('customerLevelMovCurrency', {
          is: (currency: string | null | undefined) => isPresent(currency),
          then: (schema) =>
            schema.required('Enter a MOV amount for the selected currency'),
          otherwise: (schema) => schema.nullable(),
        }),
      customerLevelMovCurrency: yup
        .string()
        .nullable()
        .when('customerLevelMovAmount', {
          is: (amount: string | null | undefined) => isPresent(amount),
          then: (schema) =>
            schema.required('Select a currency for the MOV amount'),
          otherwise: (schema) => schema.nullable(),
        }),
      visibleChatPrices: yup.boolean(),
      isPriceOverrideEnabled: yup.boolean().defined(),
      // eCommerce PO-number requirement (only surfaced when the distributor
      // supports it); applied post-create, so no cross-field rule is needed.
      isEcommercePoNumberRequired: yup.boolean(),

      // Delivery settings (edit-modal shape: per-day maps keyed by `Day`)
      hasCustomChatDeliverySettings: yup.boolean().defined(),
      deliveryDays: yup.mixed<Record<Day, boolean>>().defined(),
      cutoffType: yup.mixed<CutoffType>().defined(),
      cutoffTimes: yup.mixed<Record<Day, CutoffTime>>().defined(),
      cutoffDays: yup.mixed<Record<Day, string>>().defined(),
    },
    // MOV amount ⇄ currency reference each other; declare the edge so yup does
    // not report a cyclic dependency.
    [['customerLevelMovAmount', 'customerLevelMovCurrency']]
  );
};

const mandatoryCustomerNumberResolver = yupResolver<CreateChatFormData>(
  getCreateChatValidationSchema({ customerNumberMandatory: true }),
  { abortEarly: false },
  { raw: true }
);

const optionalCustomerNumberResolver = yupResolver<CreateChatFormData>(
  getCreateChatValidationSchema({ customerNumberMandatory: false }),
  { abortEarly: false },
  { raw: true }
);

/**
 * React Hook Form resolver for the Create Chat form.
 *
 * The customer-number requirement depends on the currently selected
 * distributor, so it is resolved per-validation from
 * `values.supplier.metaData.customerNumberMandatory` rather than being fixed at
 * form-init time. `raw: true` returns the untouched form values so the complex
 * option objects are never stripped or coerced by yup.
 */
export const createChatResolver: Resolver<CreateChatFormData> = (
  values,
  context,
  options
) => {
  const customerNumberMandatory = Boolean(
    values.supplier?.metaData?.customerNumberMandatory
  );

  return (
    customerNumberMandatory
      ? mandatoryCustomerNumberResolver
      : optionalCustomerNumberResolver
  )(values, context, options);
};
